import { AppState } from './index.js';

/**
 * Base Component Class
 * Foundation for all UI components with lifecycle methods and state management
 */

class Component {
  /**
   * Create a component
   * @param {Object} props - Component properties
   */
  constructor(props = {}) {
    this.props = props;
    this.state = {};
    this.element = null;
    this.children = new Map();
    this._isMounted = false;
    this._eventCleanups = [];
    this._stateSubscriptions = [];
  }

  // ==========================================
  // LIFECYCLE METHODS (Override in subclasses)
  // ==========================================

  /**
   * Called before component mounts
   */
  beforeMount() {}

  /**
   * Called after component mounts
   */
  afterMount() {}

  /**
   * Called before component updates
   */
  beforeUpdate() {}

  /**
   * Called after component updates
   */
  afterUpdate() {}

  /**
   * Called before component unmounts
   */
  beforeUnmount() {}

  /**
   * Bind events to the component
   * Override in subclasses
   */
  bindEvents() {}

  /**
   * Mount child components
   * Override in subclasses
   */
  mountChildren() {}

  // ==========================================
  // STATE MANAGEMENT
  // ==========================================

  /**
   * Update component state and re-render
   * @param {Object} newState - New state values
   */
  setState(newState) {
    const prevState = { ...this.state };
    this.state = { ...this.state, ...newState };

    if (this._isMounted) {
      this.beforeUpdate();
      this.update();
      this.afterUpdate();
    }
  }

  /**
   * Subscribe to AppState changes
   * @param {string} key - State key to watch
   * @param {Function} callback - Callback function
   */
  subscribeToState(key, callback) {
    const unsubscribe = AppState.subscribe(key, callback);
    this._stateSubscriptions.push(unsubscribe);
  }

  // ==========================================
  // RENDERING
  // ==========================================

  /**
   * Render the component HTML
   * Must be overridden in subclasses
   * @returns {string} HTML string
   */
  render() {
    throw new Error('Component must implement render()');
  }

  /**
   * Update the component DOM
   */
  update() {
    if (!this.element) {
      return;
    }

    // Ensure element is still in DOM before updating
    if (!this.element.parentNode) {
      console.warn('Component element no longer in DOM, skipping update');
      return;
    }

    try {
      // Cleanup before update
      this._cleanupEvents();
      this._cleanupChildren();

      // Render new HTML
      const html = this.render();
      const newElement = this.createElementFromHTML(html);

      // Replace element (wrapped in try-catch for browser extension safety)
      this.element.replaceWith(newElement);
      this.element = newElement;

      // Rebind events and remount children
      this.bindEvents();
      this.mountChildren();

      // Update icons
      this._scheduleIconUpdate();
    } catch (e) {
      // Catch errors from browser extensions accessing removed elements
      if (e.message && e.message.includes('dataset')) {
        console.warn(
          'Browser extension error during component update (safe to ignore):',
          e.message
        );
      } else {
        console.error('Component update error:', e);
      }
    }
  }

  /**
   * Mount component to a container
   * @param {Element|string} container - Container element or selector
   */
  mount(container) {
    const containerEl =
      typeof container === 'string' ? document.querySelector(container) : container;

    if (!containerEl) {
      console.error('Mount container not found:', container);
      return;
    }

    this.beforeMount();

    const html = this.render();
    this.element = this.createElementFromHTML(html);
    containerEl.appendChild(this.element);

    this._isMounted = true;
    this.bindEvents();
    this.mountChildren();
    this.afterMount();

    this._scheduleIconUpdate();
  }

  /**
   * Replace content of a container with this component
   * @param {Element|string} container - Container element or selector
   */
  mountReplace(container) {
    const containerEl =
      typeof container === 'string' ? document.querySelector(container) : container;

    if (!containerEl) {
      console.error('Mount container not found:', container);
      return;
    }

    this.beforeMount();

    const html = this.render();
    this.element = this.createElementFromHTML(html);
    containerEl.innerHTML = '';
    containerEl.appendChild(this.element);

    this._isMounted = true;
    this.bindEvents();
    this.mountChildren();
    this.afterMount();

    this._scheduleIconUpdate();
  }

  /**
   * Unmount component
   */
  unmount() {
    this.beforeUnmount();

    // Cleanup state subscriptions
    this._stateSubscriptions.forEach((unsub) => unsub());
    this._stateSubscriptions = [];

    // Cleanup events
    this._cleanupEvents();

    // Unmount children
    this._cleanupChildren();

    // Remove element
    if (this.element) {
      this.element.remove();
      this.element = null;
    }

    this._isMounted = false;
  }

  // ==========================================
  // DOM HELPERS
  // ==========================================

  /**
   * Create element from HTML string
   * Delegates to DOMUtils for consistency
   * @param {string} html - HTML string
   * @returns {Element}
   */
  createElementFromHTML(html) {
    // Delegate to DOMUtils for single source of truth
    if (typeof DOMUtils !== 'undefined') {
      return DOMUtils.createFromHTML(html);
    }
    // Fallback for edge cases (should not happen in normal operation)
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content.firstChild;
  }

  /**
   * Query selector within component
   * @param {string} selector - CSS selector
   * @returns {Element|null}
   */
  $(selector) {
    return this.element?.querySelector(selector);
  }

  /**
   * Query selector all within component
   * @param {string} selector - CSS selector
   * @returns {Element[]}
   */
  $$(selector) {
    return this.element ? Array.from(this.element.querySelectorAll(selector)) : [];
  }

  /**
   * Add event listener with auto-cleanup
   * @param {Element|string} target - Target element or selector
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   * @param {Object} options - Event options
   */
  addEventListener(target, event, handler, options = {}) {
    const element = typeof target === 'string' ? this.$(target) : target;
    if (!element) {
      return;
    }

    const boundHandler = handler.bind(this);
    element.addEventListener(event, boundHandler, options);
    this._eventCleanups.push(() => {
      element.removeEventListener(event, boundHandler, options);
    });
  }

  /**
   * Add delegated event listener
   * @param {string} event - Event name
   * @param {string} selector - Target selector
   * @param {Function} handler - Event handler
   */
  delegate(event, selector, handler) {
    if (!this.element) {
      return;
    }

    const delegatedHandler = (e) => {
      const target = e.target.closest(selector);
      if (target && this.element.contains(target)) {
        handler.call(this, e, target);
      }
    };

    this.element.addEventListener(event, delegatedHandler);
    this._eventCleanups.push(() => {
      this.element?.removeEventListener(event, delegatedHandler);
    });
  }

  // ==========================================
  // CHILD COMPONENT MANAGEMENT
  // ==========================================

  /**
   * Add a child component
   * @param {string} key - Unique key for the child
   * @param {Component} child - Child component
   * @param {Element|string} container - Container for the child
   */
  addChild(key, child, container) {
    if (this.children.has(key)) {
      this.children.get(key).unmount();
    }
    this.children.set(key, child);
    child.mount(container);
  }

  /**
   * Get a child component
   * @param {string} key - Child key
   * @returns {Component|null}
   */
  getChild(key) {
    return this.children.get(key) || null;
  }

  /**
   * Remove a child component
   * @param {string} key - Child key
   */
  removeChild(key) {
    const child = this.children.get(key);
    if (child) {
      child.unmount();
      this.children.delete(key);
    }
  }

  // ==========================================
  // PRIVATE HELPERS
  // ==========================================

  /**
   * Cleanup event listeners
   * @private
   */
  _cleanupEvents() {
    this._eventCleanups.forEach((cleanup) => cleanup());
    this._eventCleanups = [];
  }

  /**
   * Cleanup child components
   * @private
   */
  _cleanupChildren() {
    this.children.forEach((child) => child.unmount());
    this.children.clear();
  }

  /**
   * Schedule Lucide icon update
   * @private
   */
  _scheduleIconUpdate() {
    if (typeof IconManager !== 'undefined') {
      IconManager.scheduleUpdate();
    } else if (typeof lucide !== 'undefined') {
      requestAnimationFrame(() => lucide.createIcons());
    }
  }
}

export { Component };
export default Component;
